import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Badge } from "@component/badge";
import { Button } from "@component/button";
import { SnackbarProvider } from "@hook/use-snackbar";
import SkillBadge from "@component/skill-badge";
import ScoreSectionCollapsible from "@component/score-section-collapsible";
import { PDFViewer } from "@component/pdf-viewer";
import { InterviewPlayer } from "@component/interview-player";
import AiInterviewTabContent from "@container/drawer/tabs/AiInterviewTabContent";
import FormTabContent from "@container/drawer/tabs/FormTabContent";
import { getSharedCandidateProfile } from "@service/reviewer";
import { SharedCandidateProfile } from "@type/reviewer";
import { CandidateInterviewStatus } from "@type/candidate";
import { InterviewInsight } from "@type/videoInterview";
import { UserSubmissionJob } from "@type/assessments";
import { SurveyFormData, JobWorkAuthorizationMetadata } from "@type/job";
import { parseFormat } from "@util/date";
import { cn } from "@util/tailwind";
import { getMatchBadgeClasses } from "@util/job";
import {
    getEmploymentStatusLabel,
    getWorkAuthorizationLabel,
    availabilityOptions,
} from "@constant/app";
import ourteamLogo from "@asset/ourteam_logo.png";
import {
    BriefcaseIcon,
    Building2,
    GraduationCap,
    LockIcon,
    Mail,
    MapPin,
    Phone,
    AlertCircleIcon,
    FileTextIcon,
    DownloadIcon,
    ClipboardCheckIcon,
    AlertTriangleIcon,
    User,
    Code,
    Award,
    CornerDownRightIcon,
    XIcon,
} from "lucide-react";


type TopTab = "profile" | "cv" | "match-score" | "ai-interview" | "form";

function buildEligibilityItems(meta: JobWorkAuthorizationMetadata, answers: SurveyFormData) {
    const fieldConfig: {
        key: keyof JobWorkAuthorizationMetadata;
        category: string;
        getRemarks: (a: SurveyFormData) => string;
        getFlag?: (a: SurveyFormData) => { hasFlag: boolean; flagText?: string };
    }[] = [
        {
            key: "collect_employment_status",
            category: "Current Employment",
            getRemarks: (a) => {
                if (!a.employmentStatus) return "—";
                const label = getEmploymentStatusLabel(a.employmentStatus);
                return a.employmentStatusOther ? `${label} — ${a.employmentStatusOther}` : label;
            },
        },
        {
            key: "collect_expected_salary",
            category: "Expected Salary",
            getRemarks: (a) =>
                a.expectedSalary
                    ? `${a.expectedSalaryCurrency || ""} ${Number(a.expectedSalary).toLocaleString()}/month`.trim()
                    : "—",
        },
        {
            key: "collect_start_availability",
            category: "Earliest Start Date",
            getRemarks: (a) => {
                const option = availabilityOptions.find((o) => o.value === a.startAvailability);
                return option ? option.label : a.startAvailability || "—";
            },
        },
        {
            key: "collect_current_salary",
            category: "Current or Last Drawn Salary",
            getRemarks: (a) =>
                a.currentSalary
                    ? `${a.currentSalaryCurrency || ""} ${Number(a.currentSalary).toLocaleString()}/month`.trim()
                    : "—",
        },
        {
            key: "collect_relocation_willingness",
            category: "Willingness to Relocate",
            getRemarks: (a) =>
                a.relocationWillingness === "yes" ? "Yes" : a.relocationWillingness === "no" ? "No" : a.relocationWillingness || "—",
        },
        {
            key: "collect_weekend_shift_willingness",
            category: "Availability for Overtime",
            getRemarks: (a) =>
                a.shiftWillingness === "yes" ? "Yes" : a.shiftWillingness === "no" ? "No" : a.shiftWillingness || "—",
        },
        {
            key: "collect_work_authorization",
            category: "Work Authorization",
            getRemarks: (a) =>
                a.workAuthorizationStatus ? getWorkAuthorizationLabel(a.workAuthorizationStatus, "Malaysia") : "—",
            getFlag: (a) =>
                a.workAuthorizationStatus === "needs_visa"
                    ? { hasFlag: true, flagText: "Requires work visa to work in Malaysia" }
                    : { hasFlag: false },
        },
    ];

    return fieldConfig
        .filter((f) => meta[f.key])
        .map((f) => {
            const flag = f.getFlag?.(answers);
            return {
                category: f.category,
                score: 0,
                maxScore: 0,
                remarks: f.getRemarks(answers),
                hasFlag: flag?.hasFlag,
                flagText: flag?.flagText,
            };
        });
}

export default function ReviewerCandidatePage() {
    const navigate = useNavigate();
    const { candidateid } = useParams<{ candidateid: string }>();
    const [searchParams] = useSearchParams();
    const workspaceId = searchParams.get("w") || "";
    const jobId = searchParams.get("job_id") || "";

    const [isLoading, setIsLoading] = useState(true);
    const [profile, setProfile] = useState<SharedCandidateProfile | null>(null);
    const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
    const [error, setError] = useState<"access_denied" | "not_found" | null>(null);
    const [activeTab, setActiveTab] = useState<TopTab>("profile");
    const [videoDuration, setVideoDuration] = useState<number | null>(null);
    const [activeSection, setActiveSection] = useState("personal");
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [showMustHaves, setShowMustHaves] = useState(false);
    const [selectedMustHaves, setSelectedMustHaves] = useState<{ label: string; matched: boolean; explanation?: string }[]>([]);

    const selectedApplication =
        profile?.applications?.find((a) => a.application_id === selectedApplicationId) ??
        profile?.applications?.[0] ??
        null;

    // Mirror ScoreBreakdownDialog.totalScoreFromHistory: blend CV match (70%)
    // with combined AI/form assessment (30%). Falls back to raw CV match when
    // no assessment score is available.
    const totalMatchScore = (() => {
        if (!selectedApplication) return 0;
        const cvScore = Math.round(selectedApplication.match_score.total);
        // Treat 0/missing as null so the blend falls back to whichever signal exists.
        const aiScore = selectedApplication.interview.insights?.total_interview_score || null;
        const formPct = selectedApplication.form_submission?.form_score || null;
        const assessmentScore = (() => {
            if (aiScore !== null && formPct !== null) return Math.round(aiScore * 0.7 + formPct * 0.3);
            if (aiScore !== null) return Math.round(aiScore);
            if (formPct !== null) return Math.round(formPct);
            return null;
        })();
        return assessmentScore !== null
            ? Math.round(cvScore * 0.7 + assessmentScore * 0.3)
            : cvScore;
    })();

    const scrollToSection = (section: string) => {
        setActiveSection(section);
        sectionRefs.current[section]?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                const resp = await getSharedCandidateProfile(candidateid!, jobId || undefined);
                if (resp.code === 200 && resp.data) {
                    setProfile(resp.data);
                    setSelectedApplicationId(resp.data.applications?.[0]?.application_id ?? null);
                } else if (resp.code === 401) {
                    navigate(`/reviewer/verify?w=${workspaceId}&next=${encodeURIComponent(window.location.href)}`);
                } else if (resp.code === 403) {
                    setError("access_denied");
                } else {
                    setError("not_found");
                }
            } catch (err: any) {
                if (err?.response?.status === 401) {
                    navigate(`/reviewer/verify?w=${workspaceId}&next=${encodeURIComponent(window.location.href)}`);
                } else if (err?.response?.status === 403) {
                    setError("access_denied");
                } else {
                    setError("not_found");
                }
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [candidateid, workspaceId, jobId]);

    const sessionExpiresLabel = profile?.session_expires_at
        ? `Session expires: ${parseFormat(profile.session_expires_at, "D MMM YYYY")}`
        : null;

    const topTabs: { id: TopTab; label: string }[] = [
        { id: "profile", label: "Profile" },
        { id: "cv", label: "CV" },
        { id: "match-score", label: "Match Score" },
        { id: "ai-interview", label: "AI Interview" },
        { id: "form", label: "Form" },
    ];

    return (
        <SnackbarProvider>
            <div className="min-h-screen bg-white dark:bg-gray-900">
                {/* Top bar */}
                <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-10">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                        <img src={ourteamLogo} alt="ourteam" className="h-5 w-auto" />
                        <div className="flex items-center gap-3">
                            <Badge
                                variant="secondary"
                                className="bg-gray-100 text-gray-600 hover:bg-gray-100 shadow-none text-xs"
                                style={{ borderRadius: "0.75rem", paddingTop: "4px", paddingBottom: "4px" }}
                            >
                                View only
                            </Badge>
                            {sessionExpiresLabel && (
                                <span className="text-xs text-gray-400 hidden sm:block">{sessionExpiresLabel}</span>
                            )}
                        </div>
                    </div>
                </header>

                <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-32">
                            <div className="text-center space-y-3">
                                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                                <p className="text-sm text-gray-500">Loading candidate profile…</p>
                            </div>
                        </div>
                    ) : error === "access_denied" ? (
                        <div className="flex flex-col items-center justify-center py-32 text-center space-y-3">
                            <div className="p-4 rounded-full bg-gray-100">
                                <LockIcon className="h-10 w-10 text-gray-400" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900">Access denied.</h2>
                            <p className="text-sm text-gray-500 max-w-sm">This candidate profile is not available to you. Contact your HR team.</p>
                        </div>
                    ) : error === "not_found" ? (
                        <div className="flex flex-col items-center justify-center py-32 text-center space-y-3">
                            <div className="p-4 rounded-full bg-gray-100">
                                <AlertCircleIcon className="h-10 w-10 text-gray-400" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900">Profile not available.</h2>
                            <p className="text-sm text-gray-500 max-w-sm">This candidate profile is no longer available. Contact your HR team.</p>
                        </div>
                            ) : profile && selectedApplication ? (
                        <div className="bg-white dark:bg-gray-900">
                            {/* Candidate header: name/role/score on left, attribution on right */}
                            <div className="flex items-start justify-between pb-6">
                                <div className="space-y-1">
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                        {profile.candidate.name}
                                    </h3>
                                    <div className="flex items-center text-gray-500 dark:text-gray-400">
                                        <Building2 className="h-4 w-4 mr-1 flex-shrink-0" />
                                                    <span className="text-sm">{selectedApplication.applied_to.job_title}</span>
                                    </div>
                                    <div className="pt-1">
                                                    <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getMatchBadgeClasses(totalMatchScore)}`}>
                                                        {totalMatchScore}% match
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-6 ml-6 flex-shrink-0">
                                    {profile.shared_by && (
                                        <div className="text-right">
                                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Shared By</p>
                                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mt-0.5">{profile.shared_by.name}</p>
                                            {profile.shared_by.email && (
                                                <p className="text-xs text-gray-400 dark:text-gray-500">{profile.shared_by.email}</p>
                                            )}
                                        </div>
                                    )}
                                    <div className="w-px h-10 bg-gray-200 dark:bg-gray-700" />
                                    <div className="text-right">
                                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Applied To</p>
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mt-0.5">{selectedApplication.applied_to.job_title}</p>
                                                    {selectedApplication.applied_at && (
                                                        <p className="text-xs text-gray-400 dark:text-gray-500">{parseFormat(selectedApplication.applied_at, "D MMM YYYY")}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                                        {/* Application selector — shown only when candidate has more than one application */}
                                        {profile.applications.length > 1 && (
                                            <div className="mb-6 border border-gray-200 dark:border-gray-800 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50">
                                                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                                                    Applications ({profile.applications.length})
                                                </p>
                                                <div className="flex flex-wrap gap-2">
                                                    {profile.applications.map((app) => {
                                                        const isActive = app.application_id === selectedApplication.application_id;
                                                        return (
                                                            <button
                                                                key={app.application_id}
                                                                onClick={() => setSelectedApplicationId(app.application_id)}
                                                                className={cn(
                                                                    "flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm transition-colors",
                                                                    isActive
                                                                        ? "bg-white dark:bg-gray-700 border-blue-500 text-blue-600 dark:text-blue-400 shadow-sm"
                                                                        : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600"
                                                                )}
                                                            >
                                                                <span className="font-medium">{app.applied_to.job_title}</span>
                                                                <Badge
                                                                    variant="secondary"
                                                                    className="bg-gray-100 text-gray-600 hover:bg-gray-100 shadow-none text-xs"
                                                                    style={{ borderRadius: "0.5rem", paddingTop: "2px", paddingBottom: "2px" }}
                                                                >
                                                                    {app.stage}
                                                                </Badge>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                            {/* Top-level tab navigation */}
                            <div className="border-b border-gray-200 dark:border-gray-800 mb-6">
                                <div className="flex">
                                    {topTabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={cn(
                                                "py-2 px-4 text-sm font-medium relative",
                                                activeTab === tab.id
                                                    ? "text-blue-600 dark:text-blue-400"
                                                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                            )}
                                        >
                                            {tab.label}
                                            {activeTab === tab.id && (
                                                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* ── Profile Tab ── */}
                            {activeTab === "profile" && (
                                <div className="space-y-6">
                                    {/* Section navigation — exact copy of ProfileTabContent */}
                                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-1 flex flex-wrap sticky top-0 z-10">
                                        <button
                                            onClick={() => scrollToSection("personal")}
                                            className={cn("px-3 py-1.5 text-xs font-medium rounded-md transition-colors", activeSection === "personal" ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50")}
                                        >
                                            <div className="flex items-center"><User className="h-3 w-3 mr-1" /><span>Personal Information</span></div>
                                        </button>
                                        {profile.candidate.skills.length > 0 && (
                                            <button
                                                onClick={() => scrollToSection("skills")}
                                                className={cn("px-3 py-1.5 text-xs font-medium rounded-md transition-colors", activeSection === "skills" ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50")}
                                            >
                                                <div className="flex items-center"><Award className="h-3 w-3 mr-1" /><span>Skills</span></div>
                                            </button>
                                        )}
                                        {profile.candidate.experience.length > 0 && (
                                            <button
                                                onClick={() => scrollToSection("experience")}
                                                className={cn("px-3 py-1.5 text-xs font-medium rounded-md transition-colors", activeSection === "experience" ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50")}
                                            >
                                                <div className="flex items-center"><BriefcaseIcon className="h-3 w-3 mr-1" /><span>Experience</span></div>
                                            </button>
                                        )}
                                        {profile.candidate.education.length > 0 && (
                                            <button
                                                onClick={() => scrollToSection("education")}
                                                className={cn("px-3 py-1.5 text-xs font-medium rounded-md transition-colors", activeSection === "education" ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50")}
                                            >
                                                <div className="flex items-center"><GraduationCap className="h-3 w-3 mr-1" /><span>Education</span></div>
                                            </button>
                                        )}
                                    </div>

                                    {/* Personal Information — exact copy of ProfileTabContent */}
                                    <div
                                        ref={(el) => { sectionRefs.current.personal = el; }}
                                        id="personal-section"
                                        className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
                                    >
                                        <h4 className="font-medium text-base mb-2">Personal Information</h4>
                                        <div className="space-y-2 mb-4">
                                            <div className="flex items-center text-sm">
                                                <Mail className="h-5 w-5 mr-3 text-gray-600 dark:text-gray-400" />
                                                <span>{profile.candidate.email || "No Email"}</span>
                                            </div>
                                            <div className="flex items-center text-sm">
                                                <Phone className="h-5 w-5 mr-3 text-gray-600 dark:text-gray-400" />
                                                <span>{profile.candidate.phone || "No Phone Number"}</span>
                                            </div>
                                            <div className="flex items-center text-sm">
                                                <MapPin className="h-5 w-5 mr-3 text-gray-600 dark:text-gray-400" />
                                                <span>{profile.candidate.location || "No Location Provided"}</span>
                                            </div>
                                        </div>
                                        <h4 className="font-medium text-base mb-2">Professional Summary</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            {profile.candidate.professional_summary || "No professional summary provided."}
                                        </p>
                                    </div>

                                    {/* Skills — exact copy of ProfileTabContent */}
                                    {profile.candidate.skills.length > 0 && (
                                        <div
                                            ref={(el) => { sectionRefs.current.skills = el; }}
                                            id="skills-section"
                                            className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
                                        >
                                            <div className="flex items-center gap-3 mb-4 pb-2">
                                                <Code className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                                <h4 className="font-medium text-base">Skills</h4>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {profile.candidate.skills.slice(0, 20).map((skill, index) => (
                                                    <SkillBadge key={index} label={skill} />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Work Experience — exact copy of ProfileTabContent */}
                                    {profile.candidate.experience.length > 0 && (
                                        <div
                                            ref={(el) => { sectionRefs.current.experience = el; }}
                                            id="experience-section"
                                            className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
                                        >
                                            <div className="flex items-center gap-3 mb-6 pb-2">
                                                <BriefcaseIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                                <h4 className="font-medium text-base">Work Experience</h4>
                                            </div>
                                            <div className="space-y-0">
                                                <div className="relative">
                                                    {profile.candidate.experience.map((exp, index) => (
                                                        <div key={index} className={cn("relative pl-12", { "pb-8": index < profile.candidate.experience.length - 1 })}>
                                                            {index < profile.candidate.experience.length - 1 && (
                                                                <div className="absolute left-3 top-1 bottom-0 w-[2px] bg-blue-100 dark:bg-blue-900/30" />
                                                            )}
                                                            <div className="absolute left-0 top-1">
                                                                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center z-10 border-2 border-blue-500 dark:border-blue-400">
                                                                    <BriefcaseIcon className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="mb-1 relative">
                                                                    <div className="flex flex-col gap-2">
                                                                        <h5 className="font-medium text-gray-900 dark:text-gray-100 text-left w-full pr-24">
                                                                            {exp.title}
                                                                        </h5>
                                                                    </div>
                                                                    <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                                                                        {exp.duration}
                                                                    </span>
                                                                </div>
                                                                {exp.company && (
                                                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{exp.company}</p>
                                                                )}
                                                                {exp.description && (
                                                                    <p className="text-sm text-gray-600 dark:text-gray-400">{exp.description}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Education — exact copy of ProfileTabContent */}
                                    {profile.candidate.education.length > 0 && (
                                        <div
                                            ref={(el) => { sectionRefs.current.education = el; }}
                                            id="education-section"
                                            className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
                                        >
                                            <div className="flex items-center gap-3 mb-6 pb-2">
                                                <GraduationCap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                                <h4 className="font-medium text-base">Education</h4>
                                            </div>
                                            <div className="space-y-0">
                                                <div className="relative">
                                                    {profile.candidate.education.map((edu, index) => (
                                                        <div key={index} className={cn("relative pl-12", { "pb-8": index < profile.candidate.education.length - 1 })}>
                                                            {index < profile.candidate.education.length - 1 && (
                                                                <div className="absolute left-3 top-1 bottom-0 w-[2px]" style={{ backgroundColor: "#dcfce7" }} />
                                                            )}
                                                            <div className="absolute left-0 top-1">
                                                                <div className="w-6 h-6 rounded-full flex items-center justify-center z-10" style={{ backgroundColor: "#dcfce7", border: "2px solid #22c55e" }}>
                                                                    <GraduationCap className="h-3 w-3" style={{ color: "#22c55e" }} />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="mb-1 relative">
                                                                    <div className="flex flex-col gap-2">
                                                                        <h5 className="font-medium text-gray-900 dark:text-gray-100 w-full text-left pr-24">
                                                                            {edu.degree}
                                                                        </h5>
                                                                    </div>
                                                                    {edu.year && (
                                                                        <span className="text-sm text-gray-500 dark:text-gray-400 sm:absolute sm:right-0 sm:top-0">
                                                                            {edu.year}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {edu.institution && (
                                                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{edu.institution}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── CV Tab ── */}
                            {activeTab === "cv" && (
                                <div className="h-full space-y-4">
                                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                                                <FileTextIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                                                CV Details
                                            </h4>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                disabled={!profile.cv_url}
                                                onClick={() => profile.cv_url && window.open(profile.cv_url, "_blank")}
                                                className="h-8 gap-2"
                                            >
                                                <DownloadIcon className="h-4 w-4" />
                                                Download CV
                                            </Button>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="text-sm">
                                                <span className="text-gray-600 dark:text-gray-400">File Name:</span>
                                                <span className="text-gray-900 dark:text-gray-100 font-medium ml-2">
                                                    {profile.cv_filename || ""}
                                                </span>
                                            </div>
                                            <div className="text-sm">
                                                <span className="text-gray-600 dark:text-gray-400">Date Parsed:</span>
                                                <span className="text-gray-900 dark:text-gray-100 font-medium ml-2">
                                                    {profile.cv_parsed_at ? parseFormat(profile.cv_parsed_at, "D MMMM YYYY") : ""}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* CV Preview */}
                                    {profile.cv_url ? (
                                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg h-[600px]">
                                            <PDFViewer url={profile.cv_url} className="w-full h-full" />
                                        </div>
                                    ) : (
                                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg h-[400px] flex flex-col items-center justify-center text-center space-y-3">
                                            <FileTextIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                                            <p className="text-sm text-gray-500 dark:text-gray-400">CV preview not available.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Match Score Tab ── */}
                            {activeTab === "match-score" && (
                                <div className="space-y-4">
                                    {/* CV Score */}
                                    <ScoreSectionCollapsible
                                        title="CV Score"
                                                    score={selectedApplication.match_score.total}
                                        icon={<FileTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                                                    items={selectedApplication.match_score.breakdown.map((item) => {
                                                        const isMustHaveRow = item.category.toLowerCase().includes("must");
                                                        const mustHaves = isMustHaveRow
                                                            ? selectedApplication.match_score.must_have_hits.map((h) => ({
                                                                label: h.must_have_criteria,
                                                                matched: h.is_hit,
                                                                explanation: h.explanation,
                                                            }))
                                                            : undefined;
                                                        return {
                                                            category: item.category,
                                                            score: item.score,
                                                            maxScore: item.max_score,
                                                            remarks: item.remarks,
                                                            mustHaves,
                                                        };
                                                    })}
                                        onShowMustHaves={(mustHaves) => {
                                            setSelectedMustHaves(mustHaves);
                                            setShowMustHaves(true);
                                        }}
                                    />

                                    {/* Must-haves panel */}
                                    {showMustHaves && (
                                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
                                            <div className="flex items-start justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                                <div>
                                                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                                        Must-have Items
                                                    </h3>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                                        These must-have items are extracted from the job description.
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => setShowMustHaves(false)}
                                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mt-0.5"
                                                >
                                                    <XIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                                <div className="px-4 py-4 space-y-4">
                                                    {selectedMustHaves.length === 0 ? (
                                                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No must-have items available.</p>
                                                    ) : (
                                                        <>
                                                            {selectedMustHaves.filter((item) => item.matched).map((item, index) => (
                                                                <div key={`matched-${index}`} className="space-y-2">
                                                                    <div className="flex items-start gap-3">
                                                                        <div className="flex-shrink-0 mt-0.5">
                                                                            <div className="w-5 h-5 rounded-full bg-green-500 dark:bg-green-600 flex items-center justify-center">
                                                                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                                </svg>
                                                                            </div>
                                                                        </div>
                                                                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-relaxed">{item.label}</span>
                                                                    </div>
                                                                    {item.explanation && (
                                                                        <div className="flex items-start gap-2 pl-7">
                                                                            <CornerDownRightIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                                                                            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{item.explanation}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            {selectedMustHaves.filter((item) => !item.matched).map((item, index) => (
                                                                <div key={`unmatched-${index}`} className="space-y-2">
                                                                    <div className="flex items-start gap-3">
                                                                        <div className="flex-shrink-0 mt-0.5">
                                                                            <div className="w-5 h-5 rounded-full bg-red-500 dark:bg-red-600 flex items-center justify-center">
                                                                                <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                                                                </svg>
                                                                            </div>
                                                                        </div>
                                                                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-relaxed">{item.label}</span>
                                                                    </div>
                                                                    {item.explanation && (
                                                                        <div className="flex items-start gap-2 pl-7">
                                                                            <CornerDownRightIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                                                                            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{item.explanation}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                </div>
                                        </div>
                                    )}

                                    {/* Assessment Score — mirrors ScoreBreakdownDialog: only render
                                        the whole section when at least one assessment completed; only
                                        include subsections for the assessments that actually completed;
                                        and the "70%" / "30%" percentage label only appears when both
                                        are present. */}
                                    {(() => {
                                        const insights = selectedApplication.interview.insights;
                                        // Treat 0/missing as null so the blend falls back to whichever signal exists.
                                        const aiScore = insights?.total_interview_score || null;
                                        const formSub = selectedApplication.form_submission;
                                        const formPct = formSub?.form_score || null;
                                        const isAiCompleted = aiScore !== null;
                                        const isFormCompleted = formPct !== null;
                                        // No signal at all → hide the section entirely.
                                        if (!isAiCompleted && !isFormCompleted) return null;
                                        // Mirror ScoreBreakdownDialog.assessmentScore:
                                        // both → 70/30 blend; one → that score.
                                        const assessmentScore = (() => {
                                            if (isAiCompleted && isFormCompleted) return Math.round(aiScore * 0.7 + formPct * 0.3);
                                            if (isAiCompleted) return Math.round(aiScore);
                                            return Math.round(formPct ?? 0);
                                        })();
                                        return (
                                            <ScoreSectionCollapsible
                                                title="AI Screening Score"
                                                score={assessmentScore}
                                                icon={<ClipboardCheckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                                                iconBgColor="bg-blue-100 dark:bg-blue-900/30"
                                                items={[]}
                                                defaultExpanded={true}
                                                subSections={[
                                                    ...(isAiCompleted ? [{
                                                        title: "AI Interview",
                                                        percentage: isFormCompleted ? "70%" : "",
                                                        score: Math.round(aiScore ?? 0),
                                                        items: (insights?.interview_score || []).map((item) => ({
                                                            category: item.title,
                                                            score: item.score,
                                                            maxScore: item.max_score,
                                                            remarks: item.explanation,
                                                        })),
                                                    }] : []),
                                                    ...(isFormCompleted ? [{
                                                        title: "Form",
                                                        percentage: isAiCompleted ? "30%" : "",
                                                        score: Math.round(formPct ?? 0),
                                                        maxScore: 100,
                                                        remarks: formSub?.assessment_name ?? "",
                                                    }] : []),
                                                ]}
                                            />
                                        );
                                    })()}

                                    {/* Eligibility Check — same gating as ScoreBreakdownDialog
                                        (`applicant.elligibility_answers && ...`). Hide entirely
                                        when the BE didn't return any answers. */}
                                    {selectedApplication.elligibility_answers && (
                                        <ScoreSectionCollapsible
                                            title="Eligibility Check"
                                            icon={<AlertTriangleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                                            iconBgColor="bg-blue-100 dark:bg-blue-900/30"
                                            items={buildEligibilityItems(
                                                selectedApplication.eligibility?.work_auth_metadata as JobWorkAuthorizationMetadata,
                                                selectedApplication.elligibility_answers as unknown as SurveyFormData
                                            )}
                                            isScored={false}
                                        />
                                    )}
                                </div>
                            )}

                            {/* ── AI Interview Tab ── */}
                            {activeTab === "ai-interview" && (
                                <div className="space-y-4">
                                    <AiInterviewTabContent
                                                    interviewStatus={selectedApplication.interview as unknown as CandidateInterviewStatus}
                                                    interviewInsights={selectedApplication.interview.insights as unknown as InterviewInsight}
                                        isLoadingInterviewInsight={false}
                                        videoDuration={videoDuration}
                                    />
                                    <InterviewPlayer
                                        isLoadingVideo={false}
                                                    interviewStatus={selectedApplication.interview as unknown as CandidateInterviewStatus}
                                                    s3VideoLink={selectedApplication.interview.recording_url}
                                                    videoInterview={{
                                                        id: "",
                                                        conversation_id: "",
                                                        user_id: "",
                                                        interview: "",
                                                        s3_link: selectedApplication.interview.recording_url,
                                                        created_at: selectedApplication.interview.updated_at || "",
                                                        updated_at: selectedApplication.interview.updated_at || "",
                                                    }}
                                        setVideoDuration={setVideoDuration}
                                    />
                                </div>
                            )}

                            {/* ── Form Tab ── */}
                            {activeTab === "form" && (
                                            selectedApplication.form_submission ? (
                                                <FormTabContent formDisplay={selectedApplication.form_submission as UserSubmissionJob} hideExternalLink />
                                ) : (
                                    <div className="py-16 text-center text-sm text-gray-500">No form submission available.</div>
                                )
                            )}
                        </div>
                    ) : null}
                </main>
            </div>
        </SnackbarProvider>
    );
}
